import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar as CalendarIcon,
  Clock,
  AlertTriangle,
  Award,
  ChevronLeft,
  ChevronRight,
  BookOpen
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarPage = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchCalendarData();
  }, []);

  const fetchCalendarData = async () => {
    try {
      const [eventsRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/calendar/events`, { withCredentials: true }),
        axios.get(`${API}/calendar/upcoming?days=30`, { withCredentials: true })
      ]);
      setEvents(eventsRes.data);
      setUpcoming(upcomingRes.data);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty days for the start
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateStr = new Date(year, month, day).toISOString().split('T')[0];
    
    return events.filter(event => {
      const eventDate = new Date(event.date).toISOString().split('T')[0];
      return eventDate === dateStr;
    });
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="calendar-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
            Compliance Calendar
          </h1>
          <p className="text-muted-foreground mt-1">
            Track training deadlines and certificate expirations.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={prevMonth} data-testid="prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={nextMonth} data-testid="next-month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Week days header */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => {
                  const dayEvents = getEventsForDay(day);
                  const isToday = day && 
                    new Date().getDate() === day && 
                    new Date().getMonth() === currentMonth.getMonth() &&
                    new Date().getFullYear() === currentMonth.getFullYear();
                  
                  return (
                    <div 
                      key={index} 
                      className={`min-h-[80px] p-2 border rounded-lg ${
                        day ? 'bg-white' : 'bg-slate-50'
                      } ${isToday ? 'border-accent border-2' : 'border-slate-200'}`}
                    >
                      {day && (
                        <>
                          <span className={`text-sm font-medium ${isToday ? 'text-accent' : 'text-slate-700'}`}>
                            {day}
                          </span>
                          <div className="mt-1 space-y-1">
                            {dayEvents.slice(0, 2).map((event, i) => (
                              <div 
                                key={i}
                                className="text-xs px-1.5 py-0.5 rounded truncate"
                                style={{ backgroundColor: event.color + '20', color: event.color }}
                                title={event.title}
                              >
                                {event.title.substring(0, 15)}...
                              </div>
                            ))}
                            {dayEvents.length > 2 && (
                              <div className="text-xs text-muted-foreground">
                                +{dayEvents.length - 2} more
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card className="lg:col-span-4">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-accent" />
                Upcoming Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcoming.length > 0 ? (
                <div className="space-y-3">
                  {upcoming.map((event, i) => {
                    const eventDate = new Date(event.date);
                    const daysUntil = Math.ceil((eventDate - new Date()) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <div 
                        key={i} 
                        className="p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                        data-testid={`upcoming-event-${i}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: event.color + '20' }}
                            >
                              {event.type === 'training_due' ? (
                                <BookOpen className="w-5 h-5" style={{ color: event.color }} />
                              ) : (
                                <Award className="w-5 h-5" style={{ color: event.color }} />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm text-primary line-clamp-1">
                                {event.title}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {event.description}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">
                            {eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <Badge 
                            className={daysUntil <= 7 ? 'badge-danger' : daysUntil <= 14 ? 'badge-warning' : 'badge-neutral'}
                          >
                            {daysUntil <= 0 ? 'Overdue' : `${daysUntil} days`}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No upcoming deadlines</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Legend */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#F59E0B' }}></div>
                <span className="text-sm text-muted-foreground">Training Due</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#E11D48' }}></div>
                <span className="text-sm text-muted-foreground">Certificate Expiring</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CalendarPage;
